import React from 'react';
import { Box, Button, Flex, Image, Text } from '@chakra-ui/react';
import { useBizflexAppUrls } from '../../../utils/useBizflexAppUrls';
import { brandGradientButtonStyles } from '../../../lib/brandGradient';
import {
  ANDROID_APP_URL,
  IOS_APP_URL,
} from '../../../pages/Invoice/invoiceContent';

const appStoreButtonStyles = {
  bg: 'rgba(15, 23, 43, 0.72)',
  color: '#fff',
  border: '1px solid rgba(255, 255, 255, 0.45)',
  borderRadius: '12px',
  h: { base: '48px', md: '52px' },
  px: { base: '14px', md: '16px' },
  fontWeight: 400,
  fontSize: { base: '12px', md: '14px' },
  whiteSpace: 'nowrap',
  _hover: { bg: 'rgba(15, 23, 43, 0.85)' },
};

const InvoiceHero = () => {
  const { registerUrl } = useBizflexAppUrls();
  return (
    <Box
      color="#FFFFFF"
      fontFamily="Poppins"
      w="100%"
      py={{ base: '40px', md: '60px', lg: '64px' }}
      pb={{ base: '40px', md: '56px' }}
    >
      <Flex
        align="center"
        gap={{ base: '32px', md: '48px', lg: '64px' }}
        flexDir={{ base: 'column', md: 'row' }}
        w="100%"
      >
        <Box
          w={{ base: '100%', md: '50%' }}
          display="flex"
          flexDirection="column"
          alignItems={{ base: 'center', md: 'flex-start' }}
        >
          <Box
            w={{ base: '100%', md: 'fit-content' }}
            maxW="100%"
            mb={{ base: '12px', md: '16px' }}
          >
            <Text
              fontSize={{ base: '10px', md: '10px' }}
              fontWeight={400}
              color="#FFFFFF"
              textAlign="center"
              bg="#FFFFFF10"
              px={{ base: '8px', md: '12px' }}
              py={{ base: '6px', md: '4px' }}
              borderRadius="full"
              lineHeight={{ base: '1.2', md: '1.2' }}
              whiteSpace="nowrap"
              letterSpacing={{ base: '0.09em', md: 'normal' }}
              w={{ base: '100%', md: 'auto' }}
              display="block"
            >
              Fast. Professional. Automated.
            </Text>
          </Box>

          <Text
            as="h1"
            display={{ base: 'none', md: 'block' }}
            fontSize={{ base: '28px', md: '44px', lg: '52px' }}
            fontWeight={700}
            lineHeight={{ base: '36px', md: '56px', lg: '66px' }}
            textAlign="start"
            mb={{ base: '16px', md: '20px' }}
          >
            Professional
            <br />
            <Text as="span" color="#FF9D16">
              Invoicing
            </Text>{' '}
            Made
            <br />
            Simple
          </Text>

          <Text
            as="h1"
            display={{ base: 'block', md: 'none' }}
            fontSize="28px"
            fontWeight={700}
            lineHeight="36px"
            textAlign="center"
            mb="16px"
            w="100%"
          >
            Professional{' '}
            <Text as="span" color="#FF9D16">
              Invoicing
            </Text>
            <br />
            Made Simple
          </Text>

          <Text
            color="#94A3B8"
            fontSize={{ base: '12px', md: '14px', lg: '16px' }}
            lineHeight={{ base: '20px', md: '26px', lg: '28px' }}
            textAlign={{ base: 'center', md: 'start' }}
            mb={{ base: '24px', md: '36px' }}
            maxW={{ base: '100%', md: '520px' }}
            w={{ base: '100%', md: 'auto' }}
          >
            Create, send, and track professional invoices in minutes. Get paid
            faster with automated reminders and multi-currency support.
          </Text>

          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: '12px', md: '16px' }}
            justifyContent={{ base: 'stretch', md: 'flex-start' }}
            align={{ base: 'stretch', md: 'center' }}
            w={{ base: '100%', md: 'auto' }}
          >
            <Button
              as="a"
              href={registerUrl}
              target="_blank"
              rel="noreferrer"
              {...brandGradientButtonStyles}
              borderRadius="12px"
              h={{ base: '48px', md: '52px' }}
              px={{ base: '28px', md: '48px' }}
              minW={{ base: '100%', md: '240px' }}
              fontWeight={500}
              fontSize={{ base: '14px', md: '16px' }}
              w={{ base: '100%', md: 'auto' }}
            >
              Create an Invoice
            </Button>

            <Flex
              display={{ base: 'flex', md: 'contents' }}
              direction="row"
              gap="12px"
              w="100%"
            >
              <Button
                as="a"
                href={ANDROID_APP_URL}
                target="_blank"
                rel="noreferrer"
                {...appStoreButtonStyles}
                flex={{ base: 1, md: 'unset' }}
                w={{ base: 'auto', md: 'auto' }}
                leftIcon={
                  <Image
                    src="/assets/android.svg"
                    w={{ base: '16px', md: '18px' }}
                    h={{ base: '10px', md: '10px' }}
                    alt="android"
                  />
                }
              >
                Android
              </Button>

              <Button
                as="a"
                href={IOS_APP_URL}
                target="_blank"
                rel="noreferrer"
                {...appStoreButtonStyles}
                flex={{ base: 1, md: 'unset' }}
                w={{ base: 'auto', md: 'auto' }}
                leftIcon={
                  <Image
                    src="/assets/apple.svg"
                    w={{ base: '16px', md: '18px' }}
                    h={{ base: '10px', md: '10px' }}
                    alt="apple"
                  />
                }
              >
                iOS
              </Button>
            </Flex>
          </Flex>
        </Box>

        <Box
          w={{ base: '100%', md: '50%' }}
          display="flex"
          justifyContent={{ base: 'center', md: 'flex-end' }}
        >
          <Image
            src="/assets/invoice/hero-image.png"
            alt="BizFlex invoice and payments illustration"
            w="100%"
            maxW={{ base: '100%', md: '580px' }}
            h={{ base: '330px', md: '460px' }}
            objectFit="cover"
            objectPosition="center"
            borderRadius={{ base: '16px', md: '20px' }}
          />
        </Box>
      </Flex>
    </Box>
  );
};

export default InvoiceHero;
